import { DsButtonComponent } from '@alarislabs/angular-components/button';
import { DsInputComponent } from '@alarislabs/angular-components/input';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DsInputChipComponent } from '@alarislabs/angular-components/input-chip';
import {
	DsMultiSelectDropdownPresetComponent,
	DsSelectDropdownPresetComponent,
} from '@alarislabs/angular-components/dropdown';
import { DsMultiSelectComponent } from '@alarislabs/angular-components/multi-select';
import { ChoiceFormArrayComponent } from '@helpers/components/choice-form-array/choice-form-array.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AlarisDialogService, AlarisLanguageService, CustomValidators } from '@campaign-portal/components-library';
import { AitcConfirmCloseDialogComponent } from '../dialog-close/dialog-close.component';
import { provideSessionData } from '@helpers/providers/provide-session-data';
import { AiRule } from '@helpers/types/models';
import { SessionDataService } from '@helpers/services/session-data.service';
import { AiRulesService, AiSources } from '../ai-rules.service';
import { AdditionalValidators, PrefixValidators } from '@helpers/shared/validators';
import { ProductsService } from '@helpers/repo/products.service';
import { map, of, startWith } from 'rxjs';
import { EntityNotificationService } from '@helpers/services/entity-notification.service';
import { DsSelectComponent } from '@alarislabs/angular-components/select';
import { HlrConfigService } from '@helpers/providers/hlr-config.service';

@Component({
	selector: 'app-edit-ai-rules',
	templateUrl: './edit-ai-rules.component.html',
	styleUrl: './edit-ai-rules.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ReactiveFormsModule,
		TranslateModule,
		DsInputComponent,
		DsButtonComponent,
		DsInputChipComponent,
		DsMultiSelectDropdownPresetComponent,
		DsMultiSelectComponent,
		ChoiceFormArrayComponent,
		DsSelectDropdownPresetComponent,
		DsSelectComponent,
	],
	providers: [provideSessionData<AiRule>('ai_rules')],
})
export class EditAiRulesComponent implements OnInit {
	aiRuleForm = new FormGroup({
		id: new FormControl(),
		name: new FormControl<string>('', [Validators.required]),
		prefixList: new FormControl<string[]>([], {
			nonNullable: true,
			validators: [
				Validators.required,
				CustomValidators.requiredArrayOrNull,
				Validators.pattern('^\\d{1,99}$|^!?\\d{1,99}((,!?\\d{1,99})|(-\\d{1,99}))+$'),
				PrefixValidators.validatePrefix,
				AdditionalValidators.uniqueArray,
			],
		}),
		productList: new FormControl<number[] | null>(null),
		choiceList: new FormControl<string[]>([], {
			nonNullable: true,
			validators: [Validators.required, CustomValidators.requiredArrayOrNull, AdditionalValidators.arrayMaxLength(3)],
		}),
		clientProductListType: new FormControl<'INCLUSIVE' | 'EXCLUSIVE'>('INCLUSIVE', { nonNullable: true }),
	});

	defaultAitcFormValue: AiRule = {
		id: null,
		name: '',
		prefixList: [],
		productList: null,
		choiceList: [],
		clientProductListType: 'INCLUSIVE',
	};
	aiRule!: AiRule;

	clientProductListTypes = ['INCLUSIVE', 'EXCLUSIVE'];

	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly lService = inject(AlarisLanguageService);
	private readonly dialog = inject(AlarisDialogService);
	private readonly route = inject(ActivatedRoute);
	private readonly sessionDataService = inject(SessionDataService<AiRule>);
	private readonly service = inject(AiRulesService);
	public readonly products = inject(ProductsService);
	private entityNotification = inject(EntityNotificationService);
	public readonly config = inject(HlrConfigService);

	public clientProductsIds = toSignal(
		(this.products.clientProducts$ ?? of([])).pipe(map((products) => products.map((product) => product.product_id))),
		{ initialValue: [] as number[] }
	);

	public sources = signal<AiSources[]>([]);

	ngOnInit(): void {
		this.initFormData();
		this.service
			.getRelatedSources()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((res) => {
				this.sources.set(res ?? []);
			});
		this.products.loadProducts(['sms', 'hlr']).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
		this.aiRuleForm.controls.clientProductListType.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef), startWith(this.aiRuleForm.controls.clientProductListType.value))
			.subscribe((res) => {
				this.changePproductListValidator(res === 'EXCLUSIVE');
			});
	}

	save(): void {
		const aiRule = this.aiRuleForm.getRawValue();

		const payload = {
			...aiRule,
			productList: this.config.eapiEnable
				? aiRule.productList
				: aiRule.productList
					? (aiRule.productList as unknown as string)?.split(',')?.map((item) => Number(item.trim()))
					: null,
		};

		this.service
			.update(payload)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((resp) => {
				const success = !!(resp.createdId || resp.success);
				const create = payload.id === null;
				if (success) {
					this.sessionDataService.clearSession();
					this.entityNotification.showSuccess('ai_rules.source', create ? 'created' : 'updated');
					this.router.navigate(['/dipping/ai_rules']);
				}
			});
	}

	close(): void {
		if (!this.aiRuleForm.dirty) {
			this.router.navigate(['/dipping/ai_rules']);
			return;
		}
		const message = this.lService.translate('ai_rule.close_confirmation');
		this.dialog
			.open<boolean>(AitcConfirmCloseDialogComponent, {
				autoFocus: false,
				data: { message },
			})
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result: boolean | undefined) => {
				if (result) {
					this.router.navigate(['/dipping/ai_rules']);
				}
			});
	}

	reset(): void {
		this.aiRuleForm.reset(this.aiRule);
	}

	displayProduct = (productID: number): string => {
		const p = this.products.map.get(Number(productID));
		return p ? `${p.product_caption} (${p.product_id})` : '';
	};

	private initFormData(): void {
		const id = this.route.snapshot.paramMap.get('id');
		let aiRule: AiRule;
		if (id) {
			const cached = this.sessionDataService.loadFromSession();
			aiRule = cached && cached.id === Number(id) ? cached : this.defaultAitcFormValue;
		} else {
			aiRule = this.defaultAitcFormValue;
		}
		this.aiRule = aiRule;
		this.aiRuleForm.reset(this.aiRule);
	}

	private changePproductListValidator(required: boolean): void {
		const productListControl = this.aiRuleForm.controls.productList;
		const isEapi = this.config.eapiEnable;

		if (required) {
			const validators = isEapi
				? [Validators.required, CustomValidators.requiredArrayOrNull]
				: [Validators.required, Validators.pattern('^\\d+$')];
			productListControl.setValidators(validators);
		} else {
			isEapi ? productListControl.clearValidators() : productListControl.setValidators([Validators.pattern('^\\d+$')]);
		}

		productListControl.updateValueAndValidity();
	}
}
